import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { createContact } from '@/lib/crm/api';
import { ApiError } from '@/lib/api';
import type { CrmContact } from '@/lib/crm/types';

interface ContactFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  onCreated: (contact: CrmContact) => void;
}

/** Диалог добавления контактного лица к компании (AC-1.5, AC-1.6). */
export function ContactForm({
  open,
  onOpenChange,
  companyId,
  onCreated,
}: ContactFormProps) {
  const [fullName, setFullName] = useState('');
  const [position, setPosition] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFullName('');
    setPosition('');
    setPhone('');
    setEmail('');
    setIsPrimary(false);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim()) {
      setError('ФИО обязательно');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await createContact({
        company_id: companyId,
        full_name: fullName.trim(),
        position: position.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
        is_primary: isPrimary,
      });
      onCreated(created);
      reset();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'Не удалось создать контакт');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <form onSubmit={handleSubmit} data-testid="contact-form">
          <DialogHeader>
            <DialogTitle>Новый контакт</DialogTitle>
            <DialogDescription>Контактное лицо хозяйства.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-1">
              <Label htmlFor="contact-name">ФИО *</Label>
              <Input
                id="contact-name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="contact-position">Должность</Label>
                <Input
                  id="contact-position"
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  placeholder="директор / агроном"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="contact-phone">Телефон</Label>
                <Input
                  id="contact-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+7 …"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="contact-email">Email</Label>
              <Input
                id="contact-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="contact-primary"
                checked={isPrimary}
                onCheckedChange={setIsPrimary}
              />
              <Label htmlFor="contact-primary" className="cursor-pointer">
                Основной контакт
              </Label>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Отмена
            </Button>
            <Button type="submit" disabled={submitting} data-testid="contact-submit">
              {submitting ? 'Сохраняем…' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
